import { useAuth0 } from '@auth0/auth0-react';
import {
  useConsumeOrganisationSequence,
  useLocations,
  useParties,
  useSitesWithMembers,
} from '@my/directory-api/hooks';
import type { Address, Party } from '@my/directory-api';
import {
  useAssignCargoItemsToLoad,
  useCargoDefinitions,
  useCreateLoad,
  useLoads,
  useOrders,
  useRemoveLoadLine,
  useUpdateLoad,
  orderTaskKeys,
} from '@my/tms-api/hooks';
import { isSubcontractedLoad, loadSlotLabel, tmsApi } from '@my/tms-api';
import type { AssignLoadLineInput, CargoDefinition, Load, OrderTask } from '@my/tms-api';
import { cn, ErrorBanner, Loading, showToast } from '@my/ui';
import { MyIIcon } from '@my/ui/icons';
import { useQueries } from '@tanstack/react-query';
import { AlertTriangle, Clock, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { useManifestViewStore } from '@/stores/manifestView';
import { buildQueueRows, type QueueRow } from './lib/queue';
import { ManifestMap, type ManifestMapPoint, type ManifestRoute } from './components/ManifestMap';
import { LoadSuggestionsPanel } from './components/LoadSuggestionsPanel';
import { HorizonField } from '../views/HorizonField';

// ── Date helpers (planning horizon) ──────────────────────────────────────────

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Local date as `yyyy-MM-dd` for lexical comparison against task.plannedDate. */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Leg-type quick filters in the toolbar. Filter behaviour wired once defined. */
const LEG_FILTERS = ['Collect', 'Trunk', 'Deliver', 'Direct'] as const;

/** Toolbar filter chip — mirrors the Orders list filter-pill style. */
function legChipClass(active: boolean): string {
  return `rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
    active
      ? 'border-slate-700 bg-slate-700 text-white'
      : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700'
  }`;
}

export default function ManifestPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const navigate = useNavigate();

  const ordersQuery = useOrders(orgId);
  const sitesQuery = useSitesWithMembers(orgId);
  const customersQuery = useParties(orgId, 'Customer');
  const cargoDefsQuery = useCargoDefinitions(orgId);
  const locationsQuery = useLocations(orgId);
  const orders = ordersQuery.data ?? [];

  const taskQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: orderTaskKeys.byOrder(o.id),
      queryFn: () => tmsApi.getOrderTasks(o.id),
      enabled: !!orgId,
      staleTime: 30 * 1000,
    })),
  });

  const depotId = useManifestViewStore((s) => s.depotId);
  const horizonDays = useManifestViewStore((s) => s.horizonDays);
  const defaultLm = useManifestViewStore((s) => s.defaultLm);
  const setDepot = useManifestViewStore((s) => s.setDepot);
  const setHorizonDays = useManifestViewStore((s) => s.setHorizonDays);
  const setDefaultLm = useManifestViewStore((s) => s.setDefaultLm);

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [selectedLoadId, setSelectedLoadId] = useState<string | null>(null);
  const [draggedIds, setDraggedIds] = useState<string[]>([]);
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  // Which side the detail panel reflects: a focused queue row or the selected load.
  const [detailMode, setDetailMode] = useState<'task' | 'load' | null>(null);
  // MyI assistant overlay (load suggestions) — slides over the right-hand side.
  const [assistantOpen, setAssistantOpen] = useState(false);
  const legFilter = useManifestViewStore((s) => s.legFilter);
  const setLegFilter = useManifestViewStore((s) => s.setLegFilter);

  const consumeSequence = useConsumeOrganisationSequence();
  const createLoad = useCreateLoad();
  const assignToLoad = useAssignCargoItemsToLoad();
  const removeLoadLineMut = useRemoveLoadLine();
  const updateLoadMut = useUpdateLoad();

  // Depot options — organisation Sites, name-sorted. A depot is always
  // selected (it's the load's starting point), so auto-pick the first whenever
  // the stored selection is empty or stale.
  const sites = useMemo(
    () => (sitesQuery.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [sitesQuery.data],
  );
  useEffect(() => {
    const first = sites[0];
    if (!first) return;
    if (!depotId || !sites.some((s) => s.id === depotId)) setDepot(first.id);
  }, [sites, depotId, setDepot]);

  // Horizon → inclusive date range (today through today + horizonDays).
  const start = startOfToday();
  const end = addDays(start, horizonDays);
  const startYmd = ymd(start);
  const endYmd = ymd(end);

  // All loads across the horizon (any depot) — drives the summary bar and the
  // queue's "on a load" exclusion (an item with any load line drops out).
  const loadsQuery = useLoads(orgId, { fromDate: startYmd, toDate: endYmd });
  const loads = loadsQuery.data ?? [];
  const loadedItemIds = new Set<string>();
  for (const load of loads)
    for (const line of load.lines) loadedItemIds.add(line.orderTaskCargoItemId);

  // Lookups that hydrate the queue rows.
  const customersById = useMemo(() => {
    const m = new Map<string, Party>();
    for (const p of customersQuery.data ?? []) m.set(p.id, p);
    return m;
  }, [customersQuery.data]);
  const addressById = useMemo(() => {
    const m = new Map<string, Address>();
    for (const a of locationsQuery.data ?? []) m.set(a.id, a);
    return m;
  }, [locationsQuery.data]);
  // Depot starting-point city per site (via the site's address) for the Route label.
  const siteCityById = new Map(
    sites.map((s) => {
      const addr = s.addressId ? addressById.get(s.addressId) : undefined;
      return [s.id, addr?.city || s.name];
    }),
  );
  const cargoDefsById = useMemo(() => {
    const m = new Map<string, CargoDefinition>();
    for (const d of cargoDefsQuery.data ?? []) m.set(d.id, d);
    return m;
  }, [cargoDefsQuery.data]);

  // Flatten orders → one queue row per collection/delivery cargo line in the
  // horizon, then drop anything already on a load and apply the search.
  const tasksByOrderId = new Map<string, OrderTask[]>();
  orders.forEach((o, i) => tasksByOrderId.set(o.id, taskQueries[i]?.data ?? []));
  const queueRows = buildQueueRows({
    orders,
    tasksByOrderId,
    customersById,
    addressById,
    cargoDefsById,
    endYmd,
  });
  // Every unassigned task in the horizon (ignores the leg filter) — the pool
  // the suggestion engine scans.
  const unassignedRows = queueRows.filter((r) => !loadedItemIds.has(r.itemId));
  const needle = search.trim().toLowerCase();
  const visibleRows = queueRows.filter((r) => {
    if (loadedItemIds.has(r.itemId)) return false; // already on a load
    // Leg-type filter (single-select):
    //  • Collect / Direct — open collection legs.
    //  • Deliver          — open delivery legs (shown even if not yet collected;
    //                       the row carries a "not collected" warning instead).
    //  • Trunk            — open delivery legs whose goods were collected (you can
    //                       only trunk what you're holding).
    // NB: Collect≡Direct until depot-routing is modelled.
    const open = r.taskState !== 'Completed' && r.taskState !== 'Cancelled';
    if (legFilter === 'Collect' || legFilter === 'Direct') {
      if (r.role !== 'collection' || !open) return false;
    } else if (legFilter === 'Deliver') {
      if (r.role !== 'delivery' || !open) return false;
    } else if (legFilter === 'Trunk') {
      if (r.role !== 'delivery' || !open || !r.collectionCompleted) return false;
    }
    if (!needle) return true;
    return (
      r.orderNumber.toLowerCase().includes(needle) ||
      r.customerName.toLowerCase().includes(needle) ||
      r.accountCode.toLowerCase().includes(needle) ||
      r.itemName.toLowerCase().includes(needle) ||
      r.collectionAddress.toLowerCase().includes(needle) ||
      r.collectionCity.toLowerCase().includes(needle) ||
      r.deliveryAddress.toLowerCase().includes(needle) ||
      r.deliveryCity.toLowerCase().includes(needle)
    );
  });
  // Summary-bar metrics.
  const manifestedLm = loads.reduce(
    (s, l) => s + l.lines.reduce((ls, line) => ls + (line.loadingMetresM ?? 0), 0),
    0,
  );
  // Rem. LM — loading metres in the current view still to be put on a load.
  const remLm = visibleRows.reduce((s, r) => s + (r.lm ?? 0), 0);
  const estLoads = defaultLm > 0 ? Math.ceil(remLm / defaultLm) : 0;
  // Unassigned across *all* queues — every un-loaded line in the horizon (any leg).
  const unassignedAllLm = queueRows
    .filter((r) => !loadedItemIds.has(r.itemId))
    .reduce((s, r) => s + (r.lm ?? 0), 0);
  const unassignedEstLoads = defaultLm > 0 ? Math.ceil(unassignedAllLm / defaultLm) : 0;
  const allChecked = visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.itemId));
  const queueTitle =
    legFilter === 'Collect'
      ? 'Collections Queue'
      : legFilter === 'Deliver'
        ? 'Deliveries Queue'
        : legFilter === 'Trunk'
          ? 'Trunk Queue'
          : legFilter === 'Direct'
            ? 'Direct Queue'
            : 'Collections & deliveries';
  // Filter-dependent columns: Collect shows the collection side, Trunk/Deliver
  // the delivery side, Direct (and no filter) both. Set Point shows always.
  const showCollection = legFilter !== 'Trunk' && legFilter !== 'Deliver';
  const showDelivery = legFilter !== 'Collect';
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(() => (allChecked ? new Set<string>() : new Set(visibleRows.map((r) => r.itemId))));

  // ── Load building ────────────────────────────────────────────────────────
  const activeLoad = loads.find((l) => l.id === selectedLoadId) ?? null;
  const rowByItemId = new Map(queueRows.map((r) => [r.itemId, r]));
  const focusedRow = focusedItemId ? (rowByItemId.get(focusedItemId) ?? null) : null;
  const addBusy = consumeSequence.isPending || createLoad.isPending;

  // Map pins for the selected load: depot start (one colour) + every queue
  // point (another colour) + this load's assigned stops (highlighted, numbered).
  // Each pin carries hover-popup info (title + detail lines).
  const depotSite = sites.find((s) => s.id === depotId) ?? null;
  const depotAddr = depotSite?.addressId ? addressById.get(depotSite.addressId) : undefined;
  const legPlace = (r: QueueRow) =>
    (r.role === 'collection'
      ? [r.collectionCity, r.collectionCountry]
      : [r.deliveryCity, r.deliveryCountry]
    )
      .filter((c) => c && c !== '—')
      .join(', ');
  const itemLine = (r: QueueRow) =>
    `${r.quantity != null ? `${r.quantity} × ` : ''}${r.itemName}` +
    (r.lm != null ? ` · ${r.lm.toFixed(2)} LM` : '');
  const mapPoints: ManifestMapPoint[] = [];
  if (depotAddr?.latitude != null && depotAddr?.longitude != null) {
    mapPoints.push({
      id: 'depot',
      kind: 'depot',
      lat: depotAddr.latitude,
      lng: depotAddr.longitude,
      title: depotSite?.name ?? 'Depot',
      details: [
        'Depot · start',
        [depotAddr.city, depotAddr.countryIsoCode].filter(Boolean).join(', '),
      ],
    });
  }
  for (const r of visibleRows) {
    if (r.lat == null || r.lng == null) continue;
    mapPoints.push({
      id: r.itemId,
      kind: 'queue',
      lat: r.lat,
      lng: r.lng,
      title: `Order #${r.orderNumber}`,
      details: [
        itemLine(r),
        r.customerName,
        `${r.role === 'collection' ? 'Collect' : 'Deliver'} · ${legPlace(r)}`,
        r.window && r.window !== 'Any' ? `Window ${r.window}` : '',
      ],
    });
  }
  if (activeLoad) {
    activeLoad.lines
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .forEach((ln, i) => {
        const r = rowByItemId.get(ln.orderTaskCargoItemId);
        if (r?.lat == null || r?.lng == null) return;
        mapPoints.push({
          id: `line-${ln.id}`,
          kind: 'load',
          lat: r.lat,
          lng: r.lng,
          label: String(i + 1),
          title: `Order #${r.orderNumber}`,
          details: [
            itemLine(r),
            `${ln.legRole} · ${legPlace(r)}`,
            `On ${activeLoad.manifestNumber} · stop ${i + 1}`,
          ],
        });
      });
  }

  // Optimized truck route for the selected load: depot → (other stops, order
  // optimized by Azure) → the furthest stop. Drawn as a line on the map.
  let mapRoute: ManifestRoute | null = null;
  if (activeLoad && depotAddr?.latitude != null && depotAddr?.longitude != null) {
    const origin = { lat: depotAddr.latitude, lng: depotAddr.longitude };
    const stops: { lat: number; lng: number }[] = [];
    for (const ln of activeLoad.lines) {
      const r = rowByItemId.get(ln.orderTaskCargoItemId);
      if (r?.lat == null || r?.lng == null) continue;
      stops.push({ lat: r.lat, lng: r.lng });
    }
    if (stops.length > 0) {
      let di = 0;
      let best = -1;
      stops.forEach((s, i) => {
        const d = (s.lat - origin.lat) ** 2 + (s.lng - origin.lng) ** 2;
        if (d > best) {
          best = d;
          di = i;
        }
      });
      const destination = stops[di];
      if (destination) {
        mapRoute = { origin, destination, waypoints: stops.filter((_, i) => i !== di) };
      }
    }
  }

  // Route label per load: depot city → the stop furthest from the depot among
  // the load's lines. The first item added becomes the furthest by default, and
  // it recomputes as stops are added/removed. Ranking uses squared lat/lng delta
  // (good enough over a regional area); falls back to the first usable city when
  // coordinates are missing.
  const routeByLoadId = new Map<string, string>();
  for (const l of loads) {
    const originCity = siteCityById.get(l.depotSiteId) ?? '—';
    const depotOfLoad = sites.find((s) => s.id === l.depotSiteId);
    const depotA = depotOfLoad?.addressId ? addressById.get(depotOfLoad.addressId) : undefined;
    const dLat = depotA?.latitude ?? null;
    const dLng = depotA?.longitude ?? null;
    let furthestCity: string | null = null;
    let best = -1;
    for (const ln of l.lines) {
      const r = rowByItemId.get(ln.orderTaskCargoItemId);
      if (!r) continue;
      const city = r.role === 'collection' ? r.collectionCity : r.deliveryCity;
      if (!city || city === '—') continue;
      if (dLat != null && dLng != null && r.lat != null && r.lng != null) {
        const d = (r.lat - dLat) ** 2 + (r.lng - dLng) ** 2;
        if (d > best) {
          best = d;
          furthestCity = city;
        }
      } else if (furthestCity == null) {
        furthestCity = city;
      }
    }
    routeByLoadId.set(l.id, furthestCity ? `${originCity} → ${furthestCity}` : originCity);
  }

  const handleAddLoad = () => {
    if (!orgId || !depotId) return;
    consumeSequence.mutate(
      { organisationId: orgId, type: 'Manifest' },
      {
        onSuccess: (seq) =>
          createLoad.mutate(
            {
              organisationId: orgId,
              manifestNumber: seq.formatted,
              depotSiteId: depotId,
              plannedDate: startYmd,
              sequence: loads.length + 1,
              backLoad: false,
              loadingMetresCapacity: defaultLm,
            },
            {
              onSuccess: (load) => {
                setSelectedLoadId(load.id);
                setDetailMode('load');
              },
              onError: (err) => showToast({ type: 'error', message: err.message }),
            },
          ),
        onError: (err) => showToast({ type: 'error', message: err.message }),
      },
    );
  };

  // Address id of the stop furthest from the load's depot among its lines —
  // mirrors the Route label's ranking (squared lat/lng delta; first usable
  // address when coords are missing). Used to persist the load's far point.
  const furthestAddressIdFor = (load: Load): string | null => {
    const depotOfLoad = sites.find((s) => s.id === load.depotSiteId);
    const depotA = depotOfLoad?.addressId ? addressById.get(depotOfLoad.addressId) : undefined;
    const dLat = depotA?.latitude ?? null;
    const dLng = depotA?.longitude ?? null;
    let bestId: string | null = null;
    let best = -1;
    let fallbackId: string | null = null;
    for (const ln of load.lines) {
      const r = rowByItemId.get(ln.orderTaskCargoItemId);
      if (!r?.addressId) continue;
      if (dLat != null && dLng != null && r.lat != null && r.lng != null) {
        const d = (r.lat - dLat) ** 2 + (r.lng - dLng) ** 2;
        if (d > best) {
          best = d;
          bestId = r.addressId;
        }
      } else if (fallbackId == null) {
        fallbackId = r.addressId;
      }
    }
    return bestId ?? fallbackId;
  };

  // Persist the load's furthest point after its lines change. Skips the write
  // when nothing changed, and won't clobber a stored point just because the
  // lines aren't resolvable in the current horizon view (only clears it when
  // the load is genuinely empty).
  const syncFurthestPoint = (load: Load) => {
    const fid = furthestAddressIdFor(load);
    if (fid == null && load.lines.length > 0) return;
    if ((fid ?? null) === (load.furthestPointAddressId ?? null)) return;
    updateLoadMut.mutate(
      {
        loadId: load.id,
        depotSiteId: load.depotSiteId,
        plannedDate: load.plannedDate,
        sequence: load.sequence,
        furthestPointAddressId: fid,
        backLoad: load.backLoad,
        loadingMetresCapacity: load.loadingMetresCapacity,
        status: load.status,
        notes: load.notes ?? null,
      },
      { onError: (err) => showToast({ type: 'error', message: err.message }) },
    );
  };

  const assignRows = (rows: QueueRow[], load: Load) => {
    if (rows.length === 0) return;
    const base = load.lines.length;
    const lines: AssignLoadLineInput[] = rows.map((r, i) => ({
      orderTaskCargoItemId: r.itemId,
      legRole:
        legFilter === 'Trunk' ? 'Trunk' : r.role === 'collection' ? 'Collection' : 'Delivery',
      quantity: r.quantity,
      loadingMetresM: r.lm,
      sequence: base + i + 1,
    }));
    assignToLoad.mutate(
      { loadId: load.id, lines },
      {
        onSuccess: (updated) => syncFurthestPoint(updated),
        onError: (err) => showToast({ type: 'error', message: err.message }),
      },
    );
  };

  // Create a load straight from a MyI suggestion: consume a manifest number,
  // create the load at the selected depot, then assign the suggested rows
  // (legRole from each row's own leg — independent of the toolbar filter).
  const handleCreateSuggestedLoad = (rows: QueueRow[]) => {
    if (!orgId || !depotId || rows.length === 0) return;
    consumeSequence.mutate(
      { organisationId: orgId, type: 'Manifest' },
      {
        onSuccess: (seq) =>
          createLoad.mutate(
            {
              organisationId: orgId,
              manifestNumber: seq.formatted,
              depotSiteId: depotId,
              plannedDate: startYmd,
              sequence: loads.length + 1,
              backLoad: false,
              loadingMetresCapacity: defaultLm,
            },
            {
              onSuccess: (load) => {
                const lines: AssignLoadLineInput[] = rows.map((r, i) => ({
                  orderTaskCargoItemId: r.itemId,
                  legRole: r.role === 'collection' ? 'Collection' : 'Delivery',
                  quantity: r.quantity,
                  loadingMetresM: r.lm,
                  sequence: i + 1,
                }));
                assignToLoad.mutate(
                  { loadId: load.id, lines },
                  {
                    onSuccess: (updated) => syncFurthestPoint(updated),
                    onError: (err) => showToast({ type: 'error', message: err.message }),
                  },
                );
                setSelectedLoadId(load.id);
                setDetailMode('load');
                setAssistantOpen(false);
              },
              onError: (err) => showToast({ type: 'error', message: err.message }),
            },
          ),
        onError: (err) => showToast({ type: 'error', message: err.message }),
      },
    );
  };

  // Drag a queue row onto a load. Dragging a *selected* row carries the whole
  // selection; dragging an unselected row carries just that one.
  const handleRowDragStart = (itemId: string) =>
    setDraggedIds(selected.has(itemId) && selected.size > 0 ? Array.from(selected) : [itemId]);

  const handleDropOnLoad = (loadId: string) => {
    const ids = draggedIds;
    setDraggedIds([]);
    const load = loads.find((l) => l.id === loadId);
    if (!load) return;
    const rows = ids
      .filter((id) => !loadedItemIds.has(id))
      .map((id) => rowByItemId.get(id))
      .filter((r): r is QueueRow => r != null);
    if (rows.length === 0) return;
    assignRows(rows, load);
    setSelected(new Set());
  };

  if (!orgId) {
    return (
      <div className="px-2">
        <ErrorBanner
          title="No organisation selected"
          message="Pick an organisation to plan loads."
        />
      </div>
    );
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-t-lg border border-slate-200 bg-white px-3 py-2">
        <h1 className="text-sm font-semibold text-slate-400">
          <span className="text-slate-700">Manifesting</span>
        </h1>

        <Field label="Depot">
          <select
            value={depotId ?? ''}
            onChange={(e) => setDepot(e.target.value || null)}
            className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          >
            {sites.length === 0 && <option value="">No sites</option>}
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>

        <HorizonField value={horizonDays} onChange={setHorizonDays} />

        <Field label="LM">
          <input
            type="number"
            step="0.1"
            min="0"
            value={defaultLm}
            onChange={(e) => setDefaultLm(Number(e.target.value))}
            className="w-20 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm font-semibold text-slate-800 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </Field>

        <div className="mx-1 h-7 w-px bg-slate-200" />

        <div className="flex items-center gap-1.5">
          {LEG_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setLegFilter(legFilter === f ? null : f)}
              className={legChipClass(legFilter === f)}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="relative ml-auto">
          <Search
            className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
            aria-hidden="true"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs…"
            className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>

        {/* MyI presence — blinks while on the manifest screen. Opens the
            load-suggestions assistant overlay. */}
        <button
          type="button"
          onClick={() => setAssistantOpen(true)}
          aria-label="Open MyI load suggestions"
          className="shrink-0 rounded-full p-0.5 transition-colors hover:bg-slate-100"
        >
          <MyIIcon className="h-7 w-7 animate-pulse" />
        </button>
      </div>

      {/* ── Summary bar ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-x border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs">
        <Metric label="Rem. LM" value={remLm.toFixed(2)} />
        <Metric label="Est. loads" value={String(estLoads)} />
        <Metric label="Manifested" value={`${manifestedLm.toFixed(2)} LM`} />
        <Metric label="Loads" value={String(loads.length)} />
        <span className="ml-auto rounded-md bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
          Unassigned: {unassignedAllLm.toFixed(1)}m ({unassignedEstLoads}{' '}
          {unassignedEstLoads === 1 ? 'load' : 'loads'})
        </span>
      </div>

      {/* ── Body: left queue (3/4) + right column (1/4) ─────────────────── */}
      {ordersQuery.isError ? (
        <div className="px-2 pt-3">
          <ErrorBanner title="Failed to load orders" message={ordersQuery.error.message} />
        </div>
      ) : ordersQuery.isPending ? (
        <div className="p-6">
          <Loading label="Loading orders" />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-6 gap-1 pt-1">
          {/* Left — collections & deliveries queue */}
          <section className="col-span-4 flex min-h-0 flex-col">
            <QueueTable
              title={queueTitle}
              rows={visibleRows}
              selected={selected}
              allChecked={allChecked}
              focusedId={focusedItemId}
              showCollection={showCollection}
              showDelivery={showDelivery}
              onToggle={toggleOne}
              onToggleAll={toggleAll}
              onFocusRow={(id) => {
                setFocusedItemId(id);
                setDetailMode('task');
              }}
              onRowDragStart={handleRowDragStart}
              onRowDragEnd={() => setDraggedIds([])}
              onOpenOrder={(orderId) => navigate(`/orders/${orderId}`)}
            />
          </section>

          {/* Right — detail (task or load) / loads / (blank for now) */}
          <div className="col-span-2 flex min-h-0 flex-col gap-1">
            <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-white">
              <DetailPanel
                mode={detailMode}
                focusedRow={focusedRow}
                load={detailMode === 'load' ? activeLoad : null}
                rowByItemId={rowByItemId}
                onRemoveLine={(id) =>
                  removeLoadLineMut.mutate(id, {
                    onSuccess: (updated) => syncFurthestPoint(updated),
                    onError: (err) => showToast({ type: 'error', message: err.message }),
                  })
                }
                removing={removeLoadLineMut.isPending}
                dragging={draggedIds.length > 0}
                onDropOnLoad={handleDropOnLoad}
              />
            </section>
            <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-white">
              <LoadsPanel
                loads={loads}
                activeLoadId={selectedLoadId}
                onSelect={(id) => {
                  setSelectedLoadId(id);
                  setDetailMode('load');
                }}
                onAddLoad={handleAddLoad}
                addBusy={addBusy}
                routeByLoadId={routeByLoadId}
                dragging={draggedIds.length > 0}
                onDropOnLoad={handleDropOnLoad}
              />
            </section>
            {/* Map — depot start + queue points; the selected load's stops highlighted. */}
            {detailMode === 'load' && activeLoad ? (
              <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white">
                <header className="flex items-center justify-between border-b border-slate-100 px-2 py-0.5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Map
                  </span>
                  <span className="flex items-center gap-2.5 text-[10px] text-slate-400">
                    <MapLegend color="#10b981" label="Depot" />
                    <MapLegend color="#3b82f6" label="Queue" />
                    <MapLegend color="#a855f7" label="On load" />
                  </span>
                </header>
                <div className="min-h-0 flex-1">
                  <ManifestMap points={mapPoints} route={mapRoute} />
                </div>
              </section>
            ) : (
              <section className="flex min-h-0 flex-1 flex-col rounded-lg border border-slate-200 bg-white" />
            )}
          </div>
        </div>
      )}

      {/* ── MyI load-suggestions overlay — slides in over the right-hand side ── */}
      <LoadSuggestionsPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        depotLabel={depotSite?.name ?? '—'}
        depotCity={depotAddr?.city ?? depotSite?.name ?? '—'}
        dateLabel={fmtDmy(endYmd)}
        rows={unassignedRows}
        depot={{
          lat: depotAddr?.latitude ?? null,
          lng: depotAddr?.longitude ?? null,
          country: depotAddr?.countryIsoCode ?? null,
        }}
        capacityLm={defaultLm}
        onCreateLoad={handleCreateSuggestedLoad}
      />
    </div>
  );
}

/** "2026-06-09" → "09/06/26". */
function fmtDmy(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : iso;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <span className="whitespace-nowrap text-slate-500">
      {label}: <span className="font-semibold text-slate-800">{value}</span>
    </span>
  );
}

function MapLegend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span
        className="inline-block h-2 w-2 rounded-full ring-1 ring-white"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </span>
      {children}
    </div>
  );
}

/** Header cell matching the app's standard table header (Orders list `Th`). */
function QTh({ children, align }: { children: ReactNode; align?: 'right' | 'center' }) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={cn('whitespace-nowrap px-3 py-2 font-semibold', alignClass)}>{children}</th>
  );
}

type FitLevel = 'green' | 'amber' | 'red';

const FIT_DOT_CLASS: Record<FitLevel, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
};

/** Traffic-light load-fit indicator for the Fit column. Defaults to green for now. */
function FitDot({ fit }: { fit: FitLevel }) {
  return (
    <span
      className={cn('inline-block h-2.5 w-2.5 rounded-full', FIT_DOT_CLASS[fit])}
      aria-hidden="true"
    />
  );
}

function QueueTable({
  title,
  rows,
  selected,
  allChecked,
  focusedId,
  onToggle,
  onToggleAll,
  onFocusRow,
  showCollection,
  showDelivery,
  onRowDragStart,
  onRowDragEnd,
  onOpenOrder,
}: {
  title: string;
  rows: QueueRow[];
  selected: Set<string>;
  allChecked: boolean;
  focusedId: string | null;
  showCollection: boolean;
  showDelivery: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onFocusRow: (itemId: string) => void;
  onRowDragStart: (itemId: string) => void;
  onRowDragEnd: () => void;
  onOpenOrder: (orderId: string) => void;
}) {
  // checkbox + Fit + Order# + Qty + Item + LM + A/C + Customer + Window + Set Point = 10
  const colCount = 10 + (showCollection ? 3 : 0) + (showDelivery ? 3 : 0);
  return (
    <>
      <div className="mb-1 flex items-center justify-between px-0.5">
        <span className="pl-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title} ({rows.length})
        </span>
        {selected.size > 0 ? (
          <span className="text-[10px] font-medium text-blue-600">
            {selected.size} selected · drag onto a load
          </span>
        ) : (
          <span className="text-[10px] text-slate-400">Drag a row onto a load to assign</span>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-auto rounded-md border border-slate-200">
        <table className="w-full table-auto text-xs">
          <thead className="sticky top-0 z-10 bg-slate-700 text-[11px] uppercase tracking-wide text-white">
            <tr>
              <th className="w-8 whitespace-nowrap px-3 py-2 text-left font-semibold">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={onToggleAll}
                  aria-label="Select all"
                />
              </th>
              <QTh>Fit</QTh>
              <QTh>Order #</QTh>
              <QTh align="right">Qty</QTh>
              <QTh>Item</QTh>
              <QTh align="right">LM</QTh>
              <QTh>A/C</QTh>
              <QTh>Customer</QTh>
              <QTh>Window</QTh>
              {showCollection && (
                <>
                  <QTh>Collection</QTh>
                  <QTh>C.City</QTh>
                  <QTh>C.Ctry</QTh>
                </>
              )}
              {showDelivery && (
                <>
                  <QTh>Delivery</QTh>
                  <QTh>D.City</QTh>
                  <QTh>D.Ctry</QTh>
                </>
              )}
              <QTh align="right">Set Point</QTh>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-500 bg-white">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  className="px-3 py-10 text-center text-[11px] italic text-slate-400"
                >
                  No unassigned collection/delivery items in this horizon.
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const checked = selected.has(r.itemId);
                return (
                  <tr
                    key={r.itemId}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', r.itemId);
                      onRowDragStart(r.itemId);
                    }}
                    onDragEnd={onRowDragEnd}
                    onClick={() => onFocusRow(r.itemId)}
                    onDoubleClick={() => onOpenOrder(r.orderId)}
                    title="Click for detail · drag onto a load · double-click to open order"
                    className={cn(
                      'cursor-grab hover:bg-slate-50 active:cursor-grabbing',
                      checked && 'bg-blue-50/50',
                      focusedId === r.itemId && 'bg-purple-50',
                    )}
                  >
                    <td className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => onToggle(r.itemId)}
                        aria-label={`Select ${r.orderNumber}`}
                      />
                    </td>
                    {/* Fit — traffic-light load-fit indicator (default green for now). */}
                    <td className="px-2 py-1.5">
                      <FitDot fit="green" />
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-700">
                      <span className="inline-flex items-center gap-1">
                        {r.orderNumber}
                        {r.role === 'delivery' && !r.collectionCompleted && (
                          <span title="Not collected yet" className="inline-flex">
                            <AlertTriangle
                              className="h-3.5 w-3.5 text-amber-500"
                              aria-label="Not collected yet"
                            />
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                      {r.quantity ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">{r.itemName}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                      {r.lm != null ? r.lm.toFixed(2) : '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[11px] text-slate-500">
                      {r.accountCode}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">
                      {r.customerName}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3 w-3 shrink-0 text-slate-400" aria-hidden="true" />
                        {r.window}
                      </span>
                    </td>
                    {showCollection && (
                      <>
                        <td className="px-2 py-1.5 text-slate-600">
                          <div className="max-w-[14rem] truncate" title={r.collectionAddress}>
                            {r.collectionAddress}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {r.collectionCity}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {r.collectionCountry}
                        </td>
                      </>
                    )}
                    {showDelivery && (
                      <>
                        <td className="px-2 py-1.5 text-slate-600">
                          <div className="max-w-[14rem] truncate" title={r.deliveryAddress}>
                            {r.deliveryAddress}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {r.deliveryCity}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                          {r.deliveryCountry}
                        </td>
                      </>
                    )}
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                      {r.setPointC != null ? `${r.setPointC}°C` : '—'}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** "2026-06-05" → "05/06/26". */
function fmtLoadDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : iso;
}

/** Loading-metres fill bar — used/capacity, e.g. "7.70/13.40 m". */
function LmBar({ used, capacity }: { used: number; capacity: number }) {
  const pct = capacity > 0 ? Math.min(100, (used / capacity) * 100) : 0;
  const over = used > capacity;
  return (
    <span className="flex items-center gap-1.5">
      <span className="h-1.5 w-14 overflow-hidden rounded-full bg-slate-200">
        <span
          className={cn('block h-full rounded-full', over ? 'bg-red-500' : 'bg-emerald-500')}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span
        className={cn(
          'whitespace-nowrap text-[10px] tabular-nums',
          over ? 'text-red-600' : 'text-slate-500',
        )}
      >
        {used.toFixed(2)}/{capacity.toFixed(2)} m
      </span>
    </span>
  );
}

function LoadsPanel({
  loads,
  activeLoadId,
  onSelect,
  onAddLoad,
  addBusy,
  routeByLoadId,
  dragging,
  onDropOnLoad,
}: {
  loads: Load[];
  activeLoadId: string | null;
  onSelect: (id: string) => void;
  onAddLoad: () => void;
  addBusy: boolean;
  routeByLoadId: Map<string, string>;
  dragging: boolean;
  onDropOnLoad: (loadId: string) => void;
}) {
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  return (
    <>
      <header className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Loads ({loads.length})
        </span>
        <button
          type="button"
          onClick={onAddLoad}
          disabled={addBusy}
          className="flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
          New load
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        {loads.length === 0 ? (
          <p className="px-1 py-6 text-center text-[11px] italic text-slate-400">
            No loads yet — add one to start manifesting.
          </p>
        ) : (
          <table className="w-full table-auto text-xs">
            <thead className="sticky top-0 z-10 bg-slate-700 text-[10px] uppercase tracking-wide text-white">
              <tr>
                <QTh>Load #</QTh>
                <QTh>Date</QTh>
                <QTh align="right">Seq</QTh>
                <QTh>Route</QTh>
                <QTh>Trailer</QTh>
                <QTh>Truck</QTh>
                <QTh>LM</QTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-500 bg-white">
              {loads.map((l) => {
                const used = l.lines.reduce((s, ln) => s + (ln.loadingMetresM ?? 0), 0);
                const active = l.id === activeLoadId;
                const route = routeByLoadId.get(l.id) ?? '—';
                return (
                  <tr
                    key={l.id}
                    onClick={() => onSelect(l.id)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOverId(l.id);
                    }}
                    onDragLeave={() => setDragOverId((cur) => (cur === l.id ? null : cur))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOverId(null);
                      onDropOnLoad(l.id);
                    }}
                    className={cn(
                      'cursor-pointer hover:bg-slate-50',
                      active && 'bg-purple-50',
                      dragging &&
                        dragOverId === l.id &&
                        'outline outline-2 -outline-offset-2 outline-purple-500',
                    )}
                  >
                    <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-purple-700">
                      {l.manifestNumber}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">
                      {fmtLoadDate(l.plannedDate)}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                      {l.sequence}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      <span className="inline-flex items-center gap-1">
                        {route === '—' ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-medium text-purple-700 ring-1 ring-purple-200">
                            {route}
                          </span>
                        )}
                        {l.backLoad && (
                          <span className="rounded bg-blue-100 px-1 text-[9px] font-semibold text-blue-700">
                            BL
                          </span>
                        )}
                        {isSubcontractedLoad(l) && (
                          <span className="rounded bg-amber-100 px-1 text-[9px] font-semibold text-amber-700">
                            S
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">
                      {loadSlotLabel(l, 'Trailer') ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-slate-500">
                      {loadSlotLabel(l, 'Vehicle') ?? '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <LmBar used={used} capacity={l.loadingMetresCapacity} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/** Contextual right-hand detail: a focused queue line (Task) or the selected load. */
function DetailPanel({
  mode,
  focusedRow,
  load,
  rowByItemId,
  onRemoveLine,
  removing,
  dragging,
  onDropOnLoad,
}: {
  mode: 'task' | 'load' | null;
  focusedRow: QueueRow | null;
  load: Load | null;
  rowByItemId: Map<string, QueueRow>;
  onRemoveLine: (lineId: string) => void;
  removing: boolean;
  dragging: boolean;
  onDropOnLoad: (loadId: string) => void;
}) {
  if (mode === 'load' && load) {
    return (
      <LoadDetail
        load={load}
        rowByItemId={rowByItemId}
        onRemoveLine={onRemoveLine}
        removing={removing}
        dragging={dragging}
        onDropOnLoad={onDropOnLoad}
      />
    );
  }
  if (mode === 'task' && focusedRow) {
    return <TaskDetail row={focusedRow} />;
  }
  return <SkeletonPane title="Task detail" hint="Select a task from the queue." />;
}

function DField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-[4.5rem] shrink-0 pt-px text-[11px] text-slate-400">{label}</span>
      <span className="min-w-0 break-words text-xs text-slate-700">{value}</span>
    </div>
  );
}

/** Form-style detail for a focused queue line — modelled on the order ticket. */
function TaskDetail({ row }: { row: QueueRow }) {
  const direction = row.role === 'collection' ? 'INBOUND' : 'OUTBOUND';
  const itemValue = `${row.quantity != null ? `${row.quantity} × ` : ''}${row.itemName}`;
  const place = (addr: string, city: string) =>
    [addr, city].filter((p) => p && p !== '—').join(', ') || '—';
  return (
    <>
      <header className="border-b border-slate-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Task detail
      </header>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {/* Title line: Order # · customer · A/C · LM·direction badge */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-slate-100 pb-2">
          <span className="text-xs font-bold text-slate-700">#{row.orderNumber}</span>
          <span className="text-xs font-bold text-slate-600">{row.customerName}</span>
          <span className="text-slate-300">|</span>
          <span className="font-mono text-[11px] uppercase text-slate-400">{row.accountCode}</span>
          <span className="ml-auto rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">
            {row.lm != null ? `${row.lm.toFixed(2)} LM · ` : ''}
            {direction}
          </span>
        </div>
        {/* Two-column ticket body */}
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2.5">
          <DField label="Item" value={itemValue} />
          {row.setPointC != null ? (
            <DField label="Set point" value={`${row.setPointC}°C`} />
          ) : (
            <div />
          )}
          <DField label="Collection" value={place(row.collectionAddress, row.collectionCity)} />
          <DField label="Delivery" value={place(row.deliveryAddress, row.deliveryCity)} />
          <DField label="Collect at" value={row.collectionWhen} />
          <DField label="Deliver at" value={row.deliveryWhen} />
        </div>
      </div>
    </>
  );
}

/** Grid of the selected load's contents. */
function LoadDetail({
  load,
  rowByItemId,
  onRemoveLine,
  removing,
  dragging,
  onDropOnLoad,
}: {
  load: Load;
  rowByItemId: Map<string, QueueRow>;
  onRemoveLine: (lineId: string) => void;
  removing: boolean;
  dragging: boolean;
  onDropOnLoad: (loadId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const used = load.lines.reduce((s, ln) => s + (ln.loadingMetresM ?? 0), 0);
  const legLabel = (role: Load['lines'][number]['legRole']) =>
    role === 'Collection' ? 'Coll' : role === 'Delivery' ? 'Del' : 'Trunk';
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col rounded-lg',
        dragging && 'outline-dashed outline-1 -outline-offset-2 outline-purple-300',
        dragging && dragOver && 'bg-purple-50/60 outline-2 outline-purple-500',
      )}
      onDragOver={(e) => {
        if (!dragging) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        onDropOnLoad(load.id);
      }}
    >
      <header className="flex items-center justify-between border-b border-slate-100 px-2 py-0.5">
        <span className="truncate text-xs font-semibold uppercase tracking-wide text-slate-500">
          Load detail · {load.manifestNumber}
        </span>
        <span className="shrink-0 text-[11px] tabular-nums text-slate-500">
          {used.toFixed(2)}/{load.loadingMetresCapacity.toFixed(2)} m
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto">
        {load.lines.length === 0 ? (
          <p className="px-3 py-6 text-center text-[11px] italic text-slate-400">
            Empty — drag queue items onto this load.
          </p>
        ) : (
          <table className="w-full table-auto text-xs">
            <thead className="sticky top-0 z-10 bg-slate-700 text-[10px] uppercase tracking-wide text-white">
              <tr>
                <QTh>Order #</QTh>
                <QTh>Leg</QTh>
                <QTh>Item</QTh>
                <QTh align="right">Qty</QTh>
                <QTh align="right">LM</QTh>
                <QTh align="right"> </QTh>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-500 bg-white">
              {load.lines
                .slice()
                .sort((a, b) => a.sequence - b.sequence)
                .map((ln) => {
                  const r = rowByItemId.get(ln.orderTaskCargoItemId);
                  return (
                    <tr key={ln.id} className="hover:bg-slate-50">
                      <td className="whitespace-nowrap px-2 py-1.5 font-semibold text-slate-700">
                        {r?.orderNumber ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-slate-600">{legLabel(ln.legRole)}</td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-700">
                        {r?.itemName ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                        {ln.quantity ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-slate-600">
                        {ln.loadingMetresM != null ? ln.loadingMetresM.toFixed(2) : '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button
                          type="button"
                          onClick={() => onRemoveLine(ln.id)}
                          disabled={removing}
                          aria-label="Remove from load"
                          className="rounded p-0.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SkeletonPane({ title, hint }: { title: string; hint: string }) {
  return (
    <>
      {title && (
        <header className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </header>
      )}
      {hint && (
        <div className="flex flex-1 items-center justify-center p-4 text-center text-[11px] italic text-slate-400">
          {hint}
        </div>
      )}
    </>
  );
}
